import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  createMedia,
  createMultiImage,
  deleteMedia,
} from "../../features/admin/MediaSlice";
import ApiCustom from "../../utils/ApiCustom";

const mediaSchema = Yup.object().shape({
  image: Yup.mixed().required("Vui lòng chọn ảnh"),
});

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");

const hasPermission = (permission) => {
  if (Number(userId) === 1) return true;
  return getUserPermissions().includes(permission);
};

const MediaModal = ({
  media,
  onClose,
  multiImage = false,
  avatarType = false,
  onSaveMedia,
  imageableId,
  imageableType,
  imageType,
  mediaCategoryId,
  title = "hình ảnh",
  createTitle = "Thêm hình ảnh",
  detailTitle = "Chi tiết hình ảnh",
  successMessage = "Thêm hình ảnh thành công",
  deleteSuccessMessage = "Xóa hình ảnh thành công",
  showCopyUrl = true,
  showBannerAction = true,
}) => {
  const dispatch = useDispatch();
  const { error: apiError } = useSelector((state) => state.medias);

  const [image, setImage] = useState(null);
  const [images, setImages] = useState([]);
  const [preview, setPreview] = useState(media?.url || "");
  const [previewMulti, setPreviewMulti] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const validateFile = (file) => {
    const types = ["image/jpeg", "image/png", "image/gif", "image/jpg"];
    if (!types.includes(file.type)) return "Chỉ chấp nhận file ảnh";
    if (file.size > 10 * 1024 * 1024) return "File không được vượt quá 10MB";
    return null;
  };

  const handleSingleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const err = validateFile(file);
    if (err) return setError(err);

    setImage(file);
    setError(null);

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleMultiChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (files.length > 50) return setError("Tối đa 50 ảnh");

    for (const file of files) {
      const err = validateFile(file);
      if (err) return setError(err);
    }

    setImages(files);
    setImage(files[0]);
    setError(null);
    setPreviewMulti([]);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        setPreviewMulti((prev) => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await mediaSchema.validate({ image });
      setLoading(true);

      const formData = new FormData();

      if (multiImage) {
        images.forEach((file) => formData.append("files[]", file));
      } else {
        formData.append("files", image);
      }

      if (imageableType && imageType) {
        if (imageableId) formData.append("imageableId", imageableId);
        formData.append("imageableType", imageableType);
        formData.append("imageType", imageType);
      }

      if (mediaCategoryId) {
        formData.append("mediaCategoryId", mediaCategoryId);
      }

      const res = multiImage
        ? await dispatch(createMultiImage(formData)).unwrap()
        : await dispatch(createMedia(formData)).unwrap();

      if (avatarType && onSaveMedia) {
        onSaveMedia(res?.data);
      }

      toast.success(successMessage);
      onClose();
    } catch (err) {
      setError(err?.message || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!media) return;

    setLoading(true);
    const res = await dispatch(deleteMedia(media.id));
    setLoading(false);

    if (res.meta.requestStatus === "fulfilled") {
      toast.success(deleteSuccessMessage);
      onClose();
    } else {
      toast.error("Xóa không thành công");
    }
  };

  const toggleBanner = async (isBanner) => {
    try {
      await ApiCustom.put(`/change-banner/${media.id}`, { isBanner });
      toast.success("Thay đổi banner thành công");
      onClose();
    } catch {
      toast.error("Thao tác thất bại");
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-[9999] px-4">
      <div
        className={`bg-base-100 rounded-lg shadow-lg max-h-[90vh] overflow-y-auto ${
          avatarType ? "w-1/2" : media ? "w-96" : "xl:w-5/6 lg:w-11/12 w-full"
        }`}
      >
        <div className="flex justify-between items-center p-6 pb-0 sticky top-0 bg-base-100 z-10">
          <h2 className="text-xl font-semibold">
            {media ? detailTitle : createTitle}
          </h2>
          {media && showCopyUrl && (
            <button
              type="button"
              className="btn btn-outline btn-info btn-sm"
              onClick={() => {
                navigator.clipboard.writeText(media.url);
                toast.success("Đã sao chép URL");
              }}
            >
              Sao chép URL
            </button>
          )}
        </div>

        <form>
          <div className="p-6">
            {!media && (
              <input
                type="file"
                multiple={multiImage}
                accept="image/jpeg,image/png,image/gif"
                onChange={multiImage ? handleMultiChange : handleSingleChange}
                className="file-input file-input-bordered w-full"
                disabled={loading}
              />
            )}

            {preview && !multiImage && (
              <div className="mt-4 flex justify-center">
                <img
                  src={preview}
                  alt="preview"
                  className="h-40 w-40 object-contain rounded-lg"
                />
              </div>
            )}

            {media && (
              <div className="mt-4 flex justify-center">
                <img
                  src={media.url}
                  alt={title}
                  className="h-40 w-40 object-contain rounded-lg"
                />
              </div>
            )}

            {multiImage && previewMulti.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
                {previewMulti.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt="preview"
                    className="h-32 w-full object-contain rounded-lg"
                  />
                ))}
              </div>
            )}

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            {apiError && (
              <p className="text-red-500 text-sm mt-2">{apiError.message}</p>
            )}
          </div>

          {media && showBannerAction && hasPermission("MEDIA_UPDATE") && (
            <div className="px-6 flex justify-between items-center">
              <span>
                {media.isBanner ? "Hình ảnh là banner" : "Chưa là banner"}
              </span>
              <button
                type="button"
                className={`btn btn-sm ${
                  media.isBanner ? "btn-error" : "btn-success"
                }`}
                onClick={() => toggleBanner(!media.isBanner)}
                disabled={loading}
              >
                {media.isBanner ? "Huỷ banner" : "Chọn làm banner"}
              </button>
            </div>
          )}

          <div className="p-6 flex justify-between sticky bottom-0 bg-base-100">
            <button
              type="button"
              className="btn btn-sm"
              onClick={onClose}
              disabled={loading}
            >
              Đóng
            </button>

            {media ? (
              hasPermission("MEDIA_DELETE") && (
                <button
                  type="button"
                  className="btn btn-error btn-sm"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  {loading ? "Đang xóa..." : "Xóa"}
                </button>
              )
            ) : (
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={loading}
                onClick={!loading ? handleSubmit : null}
              >
                {loading ? "Đang lưu..." : "Lưu"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default MediaModal;